import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { CustomBackdrop } from 'app/components/Common/Backdrop/CustomBackdrop';
import { PageActions } from 'app/components/Common/EditPage/PageActions';
import { navigationStickActions } from 'app/components/GlobalNavigationMenu/NavigationStick.redux';
import { taskDetailsReducer, taskDetailsSliceKey } from 'app/components/Tasks/store/Tasks.redux';
import { taskDetailsSaga } from 'app/components/Tasks/store/Tasks.saga';
import { IPlacementStatusSource, IPlacementUpdatableFields } from 'app/models/Placement/PlacementDetails';
import _ from 'lodash';
import { selectUser } from 'oidc/user.selectors';
import queryString from 'query-string';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  pageAttributesActions,
  pageAttributeSliceKey,
  pageAttributesReducer,
} from './Edit/store/placementPageAttributes.redux';
import { pageAttributesSaga } from './Edit/store/placementPageAttributes.saga';
import { selectPageAttributes } from './Edit/store/placementPageAttributes.selector';
import { editableFields, editAccessors, editFromDrawerAccessors, getEditableItemIds } from './editUtils';
import { PlacementDetails } from './PlacementDetailsPage/PlacementDetails';
import {
  placementEventsReducer,
  placementEventsSliceKey,
} from './PlacementTabPanel/CredentialingTab/Store/PlacementEventDetails.redux';
import {
  placementDetailReducer,
  placementDetailsAction,
  placementDetailSliceKey,
} from '../../../../store/redux-store/placement-details/slice';
import {
  selectEditableData,
  selectAvoidPlacementParentForm,
  selectPlacementDetails,
  selectPlacementHeaderData,
  selectResetForm,
  selectComplianceStatusProgressNotes,
} from '../../../../store/redux-store/placement-details/selectors';
import { initialHeaderState, PlacementDetailsSubTab } from './utils';
import {
  placementStatusAction,
  placementStatusReducer,
  placementStatusSliceKey,
} from '../../../../store/redux-store/placement-status/slice';
import {
  placementSummaryActions,
  placementSummaryReducer,
  placementSummarySliceKey,
} from '../../../../store/redux-store/placement-summary/slice';
import { placementSummarySaga } from '../../../../store/redux-store/placement-summary/saga';
import { orderDataSliceKey, orderDataReducer } from 'app/components/Order/Store/Order.redux';
import {
  orderPreferenceReducer,
  orderPreferenceSliceKey,
} from 'app/components/Order/OrderDetails/OrderPreferences/store/OrderPreference.redux';
import { orderPreferenceSaga } from 'app/components/Order/OrderDetails/OrderPreferences/store/OrderPreference.saga';
import {
  placementDocumentsActions,
  placementDocumentsReducer,
  placementDocumentsSliceKey,
} from '../../../../store/redux-store/placement-documents/slice';
import { PlacementDocumentsSaga } from '../../../../store/redux-store/placement-documents/saga';
import { reportDetailsReducer, reportsViewSliceKey } from 'store/redux-store/report-details/slice';
import { reportDetailsSaga } from 'store/redux-store/report-details/saga';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { placementDetailsSaga } from 'store/redux-store/placement-details/saga';
import useDocumentTitle from 'utils/customHooks/useDocumentTitle';
import { trackException, trackPageView, usePageLoadingTracker } from 'app-insights/appInsightsTracking';
import { useDecision } from '@optimizely/react-sdk';
import {
  ff_peformance_enhanced_placement_profile_page,
  ff_placement_check_for_classic_packets_in_ok_to_send_status,
  ff_placement_ui_peoplesoft_sync,
  ff_placement_ui_requirement_check,
} from 'app/constants/FeatureFlags/Placement/Keys';
import { ExtensionModal } from '../PlacementExtension/ExtensionModal';
import { PreExtensionModal } from '../PlacementExtension/PreExtensionModal';
import { CommonNoDataPage } from '@AMIEWEB/Common';
import { DocEditor as ConfirmationDocEditor } from '../PlacementClientConfirmation/DocEditor';
import { DocEditor as ContractDocEditor } from '../PlacementCandidateContract/DocEditor';
import { navigationProfilesAction } from '@AMIEWEB/GlobalSearch/store/NavigationProfiles.redux';
import { ff_placement_ui_arbitration_agreement_grids } from 'app/constants/FeatureFlags/Global/keys';
import { selectCurrentModDetails } from 'store/redux-store/placement-mods/selectors';
import ComplianceStatusProgressDrawer from './Edit/Components/CSPNotes/ComplianceStatusProgressDrawer';
import { HiddenFormValueCatcher } from './Edit/HiddenFormValueCatcher';
import {
  placementSummaryConsolidatedStoreActions,
  placementSummaryConsolidatedStoreReducer,
  placementSummaryConsolidatedStoreSliceKey,
} from 'store/redux-store/placement-summary/consolidatedStoreSlice';
import { placementSummaryConsolidatedStoreSaga } from 'store/redux-store/placement-summary/consolidatedStoreSaga';
import { selectErrorStatus } from 'store/redux-store/placement-summary/consolidatedStoreSelectors';
import {
  candidateDetailReducer,
  candidateDetailSliceKey,
} from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.redux';
import { candidateDetailsSaga } from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.saga';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';

export const PlacementDetailsWrapper: React.FC = () => {
  const location = useLocation();
  const params = useParams() as unknown;
  const placementId = parseInt(params.placementId);
  useDocumentTitle({ title: `PID ${params.placementId}` });
  usePageLoadingTracker(); // Must come after the document title is set

  useInjectReducer({ key: placementStatusSliceKey, reducer: placementStatusReducer });
  useInjectReducer({ key: placementDetailSliceKey, reducer: placementDetailReducer });
  useInjectReducer({ key: placementEventsSliceKey, reducer: placementEventsReducer });
  useInjectReducer({ key: pageAttributeSliceKey, reducer: pageAttributesReducer });
  useInjectSaga({ key: taskDetailsSliceKey, saga: taskDetailsSaga });
  useInjectReducer({ key: taskDetailsSliceKey, reducer: taskDetailsReducer });
  useInjectSaga({ key: placementDetailSliceKey, saga: placementDetailsSaga });
  useInjectSaga({ key: pageAttributeSliceKey, saga: pageAttributesSaga });

  useInjectReducer({
    key: placementSummaryConsolidatedStoreSliceKey,
    reducer: placementSummaryConsolidatedStoreReducer,
  });
  useInjectSaga({ key: placementSummaryConsolidatedStoreSliceKey, saga: placementSummaryConsolidatedStoreSaga });

  useInjectReducer({ key: placementSummarySliceKey, reducer: placementSummaryReducer });
  useInjectSaga({ key: placementSummarySliceKey, saga: placementSummarySaga });
  useInjectReducer({ key: orderDataSliceKey, reducer: orderDataReducer });

  useInjectReducer({ key: orderPreferenceSliceKey, reducer: orderPreferenceReducer });
  useInjectSaga({ key: orderPreferenceSliceKey, saga: orderPreferenceSaga });

  // Documents
  useInjectReducer({ key: placementDocumentsSliceKey, reducer: placementDocumentsReducer });
  useInjectSaga({ key: placementDocumentsSliceKey, saga: PlacementDocumentsSaga });

  // Reports
  useInjectReducer({ key: reportsViewSliceKey, reducer: reportDetailsReducer });
  useInjectSaga({ key: reportsViewSliceKey, saga: reportDetailsSaga });

  useInjectReducer({ key: candidateDetailSliceKey, reducer: candidateDetailReducer });
  useInjectSaga({ key: candidateDetailSliceKey, saga: candidateDetailsSaga });

  const [enablePeoplesoft] = useDecision(ff_placement_ui_peoplesoft_sync);
  const [requirementCheckFlag] = useDecision(ff_placement_ui_requirement_check);
  const [checkClassicPacketInOkToSendFlag] = useDecision(ff_placement_check_for_classic_packets_in_ok_to_send_status);
  const [arbitrationAgreementFlag] = useDecision(ff_placement_ui_arbitration_agreement_grids);
  const [performanceOptimizedPageLoad] = useDecision(ff_peformance_enhanced_placement_profile_page);

  const { tab, view } = queryString.parse(window.location.search);

  const hiddentSubmitBtn = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    trackPageView({ name: `PID ${placementId}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    header: { lastUpdatedAt },
    requiredDataLoadStatus,
    timestamp,
  } = useSelector(selectPlacementDetails);

  const editableData = useSelector(selectEditableData);
  const placementHeader = useSelector(selectPlacementHeaderData);
  const resetForm = useSelector(selectResetForm);
  const avoidParentForm = useSelector(selectAvoidPlacementParentForm);

  const _avoidParentForm = React.useMemo(() => (!!view ? false : avoidParentForm), [avoidParentForm, view]);
  const { userInfo } = useSelector(selectUser);
  const { accessibleItems, glanceDrawerState, currentFocus, editOrderStack, popperOpen } =
    useSelector(selectPageAttributes);
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const { openDrawer } = useSelector(selectComplianceStatusProgressNotes);
  const { promiseInProgress: isSubmitting } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.updateAllDetails,
    delay: 0,
  });

  /** Start - Provision to hide actions */
  const { promiseInProgress: headerIsLoading } = usePromiseTracker({ area: 'placement-header-call', delay: 0 });
  const { promiseInProgress: glanceIsLoading } = usePromiseTracker({ area: 'placement-quick-glance', delay: 0 });
  const { promiseInProgress: fileoutIsLoading } = usePromiseTracker({ area: 'candidate-records-right', delay: 0 });
  const { promiseInProgress: comparisonIsLoading } = usePromiseTracker({
    area: 'placement-comparison-order',
    delay: 0,
  });
  const { promiseInProgress: licenseIsLoading } = usePromiseTracker({
    area: 'placement-candidate-license-list',
    delay: 0,
  });
  const { promiseInProgress: educationIsLoading } = usePromiseTracker({
    area: 'placement-candidate-education-list',
    delay: 0,
  });
  const { promiseInProgress: credentialIsLoading } = usePromiseTracker({
    area: 'placement-credential-certifications',
    delay: 0,
  });
  const { promiseInProgress: loadingBulkDetails } = usePromiseTracker({
    area: 'placement-consolidated-bulk-section-details-data',
    delay: 0,
  });
  /** End - Provision to hide actions */

  const errorStatus = useSelector(selectErrorStatus);

  const { handleSubmit, reset, formState, watch, ...restFormMethods } = useForm<IPlacementUpdatableFields>({
    defaultValues: {
      requestedShift: null,
      placementShift: null,
      bestTimeToCall: null,
      travelPartner: null,
      placementDate: initialHeaderState.placementDate,
      placementStartDate: null,
      placementEndDate: null,
      availabilityDate: initialHeaderState.availabilityDate,
      sellingPoints: null,
      requestedTimeOff: {
        isRequestedTimeOffMakeUp: false,
        requestedTimeOff: [],
        requestedTimeOffMode: undefined,
        requestedTimeOffNote: '',
      },
      ssn: undefined,
      dob: undefined,
      vmsReqNo: null,
      contractHours: null,
      numberOfWeeks: null,
    },
    shouldUnregister: false,
  });
  const travelsWithPartnerStatus = watch('travelsWithPartner');
  const dispatch = useDispatch();
  const areValidateTimestampsReady = Boolean(timestamp?.placementTimestamp && timestamp?.candidateTimestamp);

  const handleUpdates = React.useCallback(
    updates => {
      if (userInfo) {
        // Guard against cross-placement writes while prospecting (up/down) navigation is
        // in flight: the loaded header must match the placement in the URL before we persist
        // form edits, otherwise a previous placement's values could be saved to another id.
        if (placementHeader?.placementId !== placementId) {
          trackException({
            exception: new Error('Aborted placement update: URL/header placement id mismatch'),
            properties: {
              name: 'PlacementCrossWriteGuard',
              functionName: 'handleUpdates',
              area: 'PlacementDetailsWrapper.tsx',
              urlPlacementId: placementId,
              headerPlacementId: placementHeader?.placementId,
            },
          });
          return;
        }
        const dirtyFields = _.cloneDeep(formState.dirtyFields);
        dispatch(
          placementDetailsAction.updatePlacementAllAction({
            placementId,
            dirtyFields: dirtyFields,
            updates: updates,
            validateConflict: {
              source: IPlacementStatusSource.pageActions,
            },
            requirementCheckFlagEnabled: requirementCheckFlag.enabled,
          }),
        );
        dispatch(pageAttributesActions.setEditOrderStack([]));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState.dirtyFields, placementId, placementHeader?.placementId, requirementCheckFlag.enabled, userInfo],
  );

  useEffect(() => {
    if (Number.isInteger(placementId) && placementId > 0) {
      dispatch(navigationStickActions.setSelectedSubMenu(undefined));
      dispatch(navigationStickActions.setSelectedMenuByKey('placement'));
      dispatch(
        placementDetailsAction.onInitAction({
          placementId,
          peoplesoftEnabled: enablePeoplesoft?.enabled,
          requirementCheckFlagEnabled: requirementCheckFlag.enabled,
          checkClassicPacketInOkToSendEnabled: checkClassicPacketInOkToSendFlag?.enabled,
          arbitrationAgreementFlagEnabled: arbitrationAgreementFlag?.enabled,
          isPerformanceOptimized: performanceOptimizedPageLoad?.enabled,
        }),
      );
    } else {
      dispatch(placementDetailsAction.setRequiredDataLoadStatus(false));
    }
    return () => {
      const url = location.pathname;
      dispatch(placementSummaryConsolidatedStoreActions.reset());
      if (!url?.includes('margin')) dispatch(placementDetailsAction.reset());
      dispatch(placementSummaryActions.reset());
      dispatch(pageAttributesActions.reset());
      dispatch(placementStatusAction.reset());
      dispatch(placementDocumentsActions.reset());
      dispatch(placementMarginScenarioActions.reset());
      dispatch(navigationProfilesAction.setNavigationFlag({ navigationFromGrid: false }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementId]);

  useEffect(() => {
    reset(editableData, { keepDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdatedAt, resetForm]);

  useEffect(() => {
    // Discard any in-memory (dirty) form edits the moment the displayed placement changes
    // (e.g. prospecting up/down). Redux slices are reset separately, but react-hook-form state
    // is not redux and would otherwise linger until the next placement's data lands, allowing a
    // stale value to be saved against a different placement id.
    reset(undefined, { keepDirty: false, keepValues: false, keepErrors: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementId]);

  useEffect(() => {
    dispatch(
      pageAttributesActions.setEditableIds(getEditableItemIds(tab, travelsWithPartnerStatus, glanceDrawerState)),
    );
    const avoidForm = tab == PlacementDetailsSubTab.CREDENTIALING || tab === 3;
    dispatch(placementDetailsAction.setAvoidPlacementParentForm(avoidForm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, glanceDrawerState, travelsWithPartnerStatus]);

  const undoLastEdit = () => {
    const keys = _.cloneDeep(editOrderStack);
    const lastEditedKey = keys.pop();
    if (lastEditedKey) {
      if (lastEditedKey === editableFields.travelsWithPartner) {
        dispatch(
          pageAttributesActions.setEditOrderStack(
            editOrderStack.filter(
              key => ![editableFields.travelsWithPartner, editableFields.travelPartner].includes(key),
            ),
          ),
        );
      } else {
        dispatch(pageAttributesActions.popFromEditOrderStack());
      }
      const accessor = (glanceDrawerState ? editFromDrawerAccessors : editAccessors).find(
        item => item.value === lastEditedKey,
      );
      if (accessor) {
        document.getElementById(accessor.id)?.focus();
        dispatch(pageAttributesActions.setCurrentFocus(accessor.id));
      }
    }
  };

  const handleSubmissionClick = React.useCallback(() => {
    hiddentSubmitBtn.current?.click?.();
  }, []);

  const detailsRenderer = React.useMemo(
    () => (
      <>
        {view === 'client-confirmation-editor' ? (
          <ConfirmationDocEditor />
        ) : view === 'candidate-contract-editor' ? (
          <ContractDocEditor />
        ) : _avoidParentForm ? null : (
          <PlacementDetails />
        )}
        <HiddenFormValueCatcher />

        {_avoidParentForm ? <PlacementDetails /> : null}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleUpdates, view, _avoidParentForm],
  );

  return !(Number.isInteger(placementId) && placementId > 0) || errorStatus ? (
    <CommonNoDataPage message={`Unable to find information about selected placement`} />
  ) : (
    <>
      <FormProvider {...{ handleSubmit, reset, formState, watch, ...restFormMethods }}>
        {detailsRenderer}
        {tab == PlacementDetailsSubTab.CREDENTIALING || selectedModsDetails || !!view || openDrawer ? null : (
          <form onSubmit={handleSubmit(handleUpdates)}>
            <PageActions
              onClearChanges={() => reset(editableData)}
              disableSaveBtn={
                requiredDataLoadStatus !== true ||
                placementHeader?.placementId !== placementId ||
                !areValidateTimestampsReady ||
                Object.keys(formState.dirtyFields).length === 0 ||
                Object.keys(formState.errors).length > 0
              }
              disableClearBtn={Object.keys(formState.dirtyFields).length === 0}
              disableCancelButton={true}
              isPageDirty={Object.keys(formState.dirtyFields).length !== 0}
              hideActions={
                requiredDataLoadStatus === undefined ||
                headerIsLoading ||
                glanceIsLoading ||
                fileoutIsLoading ||
                comparisonIsLoading ||
                licenseIsLoading ||
                educationIsLoading ||
                credentialIsLoading ||
                loadingBulkDetails
              }
              accessibleItems={accessibleItems}
              undoLastEdit={undoLastEdit}
              currentFocus={currentFocus}
              setCurrentFocus={elementId => dispatch(pageAttributesActions.setCurrentFocus(elementId))}
              keydownListnerDependancies={[editOrderStack]}
              disableAllTabbing={popperOpen}
              withoutSubmitBtn
              handleSubmit={handleSubmissionClick}
            />
            <button type="submit" ref={hiddentSubmitBtn} style={{ display: 'none' }} />
          </form>
        )}
        <CustomBackdrop open={isSubmitting} />
      </FormProvider>
      <ExtensionModal />
      <ComplianceStatusProgressDrawer />
      <PreExtensionModal />
    </>
  );
};
